/**
 * VaultConfirmModal: Custom branded confirmation dialog.
 * Replaces browser confirm() with the Vault design language.
 */
import { type ReactNode } from 'react';
import { AlertTriangle, X, Trash2, Info, ShieldAlert } from 'lucide-react';

export type ConfirmVariant = 'danger' | 'warning' | 'info';

export interface VaultConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  onConfirm: () => void;
  onCancel: () => void;
}

export function VaultConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
}: VaultConfirmModalProps) {
  if (!isOpen) return null;

  const iconMap = {
    danger: <Trash2 size={24} />,
    warning: <AlertTriangle size={24} />,
    info: <Info size={24} />,
  };

  return (
    <div className="vault-modal-overlay" onClick={onCancel}>
      <div
        className="vault-modal vault-confirm-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Visual header strip */}
        <div className={`vault-cfm-strip ${variant}`} />

        {/* Close button */}
        <button className="vault-cfm-close" onClick={onCancel}>
          <X size={16} />
        </button>

        {/* Body */}
        <div className="vault-cfm-body">
          <div className={`vault-cfm-icon ${variant}`}>
            {iconMap[variant]}
          </div>
          <h3 className="vault-cfm-title">{title}</h3>
          <div className="vault-cfm-message">{message}</div>
        </div>

        {/* Actions */}
        <div className="vault-cfm-actions">
          <button className="vault-cfm-cancel" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            className={`vault-cfm-confirm ${variant}`}
            onClick={onConfirm}
            autoFocus
          >
            <ShieldAlert size={14} />
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
